/**
 * Sidebar v2 thread overlay state: the pure reducers for per-thread visit,
 * pin, order-key, snooze, linked-PR, and changed-file UI state, plus the
 * persisted snooze sanitizer.
 *
 * Split out of uiStateStore.ts (same origin) for the file-size ratchet; the
 * store, project reducers, and persistence stay there. Type-only import of
 * the shared UiState/SidebarThreadSnooze shapes, so no runtime cycle.
 */

import type { SidebarThreadSnooze, UiState } from "./uiStateStore";

/** Copy a snooze entry when both timestamp fields are nonempty strings; this does not validate timestamp syntax. Persisted entries are untrusted, so a null or non-object value is dropped before any field read. */
function sanitizeSnoozeEntry(snooze: unknown): SidebarThreadSnooze | null {
  if (!snooze || typeof snooze !== "object") return null;
  const entry = snooze as Partial<SidebarThreadSnooze>;
  if (
    typeof entry.snoozedAt !== "string" ||
    entry.snoozedAt.length === 0 ||
    typeof entry.snoozedUntil !== "string" ||
    entry.snoozedUntil.length === 0
  ) {
    return null;
  }
  return { snoozedAt: entry.snoozedAt, snoozedUntil: entry.snoozedUntil };
}

/** Build the persisted snooze map from nonempty thread keys whose entries have both timestamp strings. */
export function sanitizePersistedThreadSnooze(value: unknown): Record<string, SidebarThreadSnooze> {
  if (!value || typeof value !== "object") {
    return {};
  }
  const sanitized: Record<string, SidebarThreadSnooze> = {};
  for (const [threadId, snooze] of Object.entries(value)) {
    if (typeof threadId !== "string" || threadId.length === 0) continue;
    const entry = sanitizeSnoozeEntry(snooze);
    if (entry) sanitized[threadId] = entry;
  }
  return sanitized;
}

export function markThreadVisited(state: UiState, threadId: string, visitedAt?: string): UiState {
  const at = visitedAt ?? new Date().toISOString();
  const visitedAtMs = Date.parse(at);
  const previousVisitedAt = state.threadLastVisitedAtById[threadId];
  const previousVisitedAtMs = previousVisitedAt ? Date.parse(previousVisitedAt) : NaN;
  if (
    Number.isFinite(previousVisitedAtMs) &&
    Number.isFinite(visitedAtMs) &&
    previousVisitedAtMs >= visitedAtMs
  ) {
    return state;
  }
  return {
    ...state,
    threadLastVisitedAtById: {
      ...state.threadLastVisitedAtById,
      [threadId]: at,
    },
  };
}

export function markThreadUnread(
  state: UiState,
  threadId: string,
  latestTurnCompletedAt: string | null | undefined,
): UiState {
  if (!latestTurnCompletedAt) {
    return state;
  }
  const latestTurnCompletedAtMs = Date.parse(latestTurnCompletedAt);
  if (Number.isNaN(latestTurnCompletedAtMs)) {
    return state;
  }
  const unreadVisitedAt = new Date(latestTurnCompletedAtMs - 1).toISOString();
  if (state.threadLastVisitedAtById[threadId] === unreadVisitedAt) {
    return state;
  }
  return {
    ...state,
    threadLastVisitedAtById: {
      ...state.threadLastVisitedAtById,
      [threadId]: unreadVisitedAt,
    },
  };
}

/** Remove visit, file expansion, pin, pull-request, order, and snooze state for one scoped thread key. */
export function clearThreadUi(state: UiState, threadId: string): UiState {
  const hasVisitedState = threadId in state.threadLastVisitedAtById;
  const hasChangedFilesState = threadId in state.threadChangedFilesExpandedById;
  const hasPinnedState = threadId in state.pinnedThreadById;
  const hasLinkedPrState = threadId in state.threadLinkedPrById;
  const hasPinOrderState = threadId in state.pinOrderKeyById;
  const hasActiveOrderState = threadId in state.activeOrderKeyById;
  const hasSnoozeState = threadId in state.threadSnoozeById;
  if (
    !hasVisitedState &&
    !hasChangedFilesState &&
    !hasPinnedState &&
    !hasLinkedPrState &&
    !hasPinOrderState &&
    !hasActiveOrderState &&
    !hasSnoozeState
  ) {
    return state;
  }
  const nextThreadLastVisitedAtById = { ...state.threadLastVisitedAtById };
  const nextThreadChangedFilesExpandedById = { ...state.threadChangedFilesExpandedById };
  const nextPinnedThreadById = { ...state.pinnedThreadById };
  const nextThreadLinkedPrById = { ...state.threadLinkedPrById };
  const nextPinOrderKeyById = { ...state.pinOrderKeyById };
  const nextActiveOrderKeyById = { ...state.activeOrderKeyById };
  const nextThreadSnoozeById = { ...state.threadSnoozeById };
  delete nextThreadLastVisitedAtById[threadId];
  delete nextThreadChangedFilesExpandedById[threadId];
  delete nextPinnedThreadById[threadId];
  delete nextThreadLinkedPrById[threadId];
  delete nextPinOrderKeyById[threadId];
  delete nextActiveOrderKeyById[threadId];
  delete nextThreadSnoozeById[threadId];
  return {
    ...state,
    threadLastVisitedAtById: nextThreadLastVisitedAtById,
    threadChangedFilesExpandedById: nextThreadChangedFilesExpandedById,
    pinnedThreadById: nextPinnedThreadById,
    threadLinkedPrById: nextThreadLinkedPrById,
    pinOrderKeyById: nextPinOrderKeyById,
    activeOrderKeyById: nextActiveOrderKeyById,
    threadSnoozeById: nextThreadSnoozeById,
  };
}

export function setThreadPinned(state: UiState, threadId: string, pinned: boolean): UiState {
  const currentlyPinned = state.pinnedThreadById[threadId] === true;
  if (currentlyPinned === pinned) {
    return state;
  }
  if (pinned) {
    return {
      ...state,
      pinnedThreadById: {
        ...state.pinnedThreadById,
        [threadId]: true,
      },
    };
  }
  const nextPinnedThreadById = { ...state.pinnedThreadById };
  delete nextPinnedThreadById[threadId];
  return {
    ...state,
    pinnedThreadById: nextPinnedThreadById,
  };
}

export function toggleThreadPinned(state: UiState, threadId: string): UiState {
  return setThreadPinned(state, threadId, state.pinnedThreadById[threadId] !== true);
}

/** Sidebar v2 overlay: write (or clear, with null) one thread's pin order key.
 * Unpinning through `setThreadPinned(false)` does not clear the key, so a
 * re-pin without an explicit slot keeps its old arrangement slot until a
 * drag assigns a fresh one — matching upstream's server-side field behavior. */
export function setThreadPinOrderKey(
  state: UiState,
  threadId: string,
  orderKey: string | null,
): UiState {
  const current = state.pinOrderKeyById[threadId];
  if (orderKey === null) {
    if (current === undefined) return state;
    const next = { ...state.pinOrderKeyById };
    delete next[threadId];
    return { ...state, pinOrderKeyById: next };
  }
  if (current === orderKey) return state;
  return { ...state, pinOrderKeyById: { ...state.pinOrderKeyById, [threadId]: orderKey } };
}

/** Sidebar v2 overlay: write (or clear, with null) one thread's manual
 * active-list order key. */
export function setThreadActiveOrderKey(
  state: UiState,
  threadId: string,
  orderKey: string | null,
): UiState {
  const current = state.activeOrderKeyById[threadId];
  if (orderKey === null) {
    if (current === undefined) return state;
    const next = { ...state.activeOrderKeyById };
    delete next[threadId];
    return { ...state, activeOrderKeyById: next };
  }
  if (current === orderKey) return state;
  return { ...state, activeOrderKeyById: { ...state.activeOrderKeyById, [threadId]: orderKey } };
}

/** Sidebar v2 overlay: snooze (or wake, with null) one thread. Snooze pins
 * nothing on its own; upstream's rules keep pin and snooze independent so a
 * snoozed thread can stay pinned beneath the shelf. */
export function setThreadSnooze(
  state: UiState,
  threadId: string,
  snooze: SidebarThreadSnooze | null,
): UiState {
  if (snooze === null) {
    if (!(threadId in state.threadSnoozeById)) return state;
    const next = { ...state.threadSnoozeById };
    delete next[threadId];
    return { ...state, threadSnoozeById: next };
  }
  const current = state.threadSnoozeById[threadId];
  if (
    current &&
    current.snoozedAt === snooze.snoozedAt &&
    current.snoozedUntil === snooze.snoozedUntil
  ) {
    return state;
  }
  return { ...state, threadSnoozeById: { ...state.threadSnoozeById, [threadId]: snooze } };
}

export function setThreadLinkedPr(state: UiState, threadId: string, prUrl: string): UiState {
  if (state.threadLinkedPrById[threadId] === prUrl) {
    return state;
  }
  return {
    ...state,
    threadLinkedPrById: {
      ...state.threadLinkedPrById,
      [threadId]: prUrl,
    },
  };
}

export function clearThreadLinkedPr(state: UiState, threadId: string): UiState {
  if (!(threadId in state.threadLinkedPrById)) {
    return state;
  }
  const nextThreadLinkedPrById = { ...state.threadLinkedPrById };
  delete nextThreadLinkedPrById[threadId];
  return {
    ...state,
    threadLinkedPrById: nextThreadLinkedPrById,
  };
}

export function setThreadChangedFilesExpanded(
  state: UiState,
  threadId: string,
  turnId: string,
  expanded: boolean,
): UiState {
  const currentThreadState = state.threadChangedFilesExpandedById[threadId] ?? {};
  if (currentThreadState[turnId] === expanded) {
    return state;
  }

  return {
    ...state,
    threadChangedFilesExpandedById: {
      ...state.threadChangedFilesExpandedById,
      [threadId]: {
        ...currentThreadState,
        [turnId]: expanded,
      },
    },
  };
}
